import { action, computed, observable } from 'mobx'
import { ModalViewModel } from '@/infra/ModalViewModel'
import {
  getSupportingItemSpecKey,
  type InventoryItemSupportingItem,
  type InventoryItemSupportingItemSpec,
} from '@/features/inventory/InventoryItem'
import { getSupportingItemSpecDisplayName } from '@/features/inventory/inventory-item-display'
import { type DocumentType } from '@/features/documents/DocumentType'

export class ManageSupportingItemsModalFormViewModel {
  readonly modal = new ModalViewModel()

  @observable
  accessor documentTypeSupportingItemVms: SupportingItemViewModel[] = []

  @observable
  accessor inputSupportingItemVms: SupportingItemViewModel[] = []

  constructor(
    private readonly _getDocumentTypes: () => DocumentType[],
    private readonly _getSupportingItems: () => InventoryItemSupportingItem[],
    private readonly _setSupportingItems: (
      items: InventoryItemSupportingItem[],
    ) => void,
  ) {}

  @computed
  get inputSupportingItems() {
    return [
      {
        type: 'BusinessInfo',
      },
      {
        type: 'BusinessRefundMethodInfo',
      },
      {
        type: 'DependentsInfo',
      },
      {
        type: 'Notes',
      },
      {
        type: 'PersonalInfo',
      },
      {
        type: 'PersonalRefundMethodInfo',
      },
      {
        type: 'SpousalInfo',
      },
    ] as InventoryItemSupportingItemSpec[]
  }

  @computed
  get documentTypes() {
    return this._getDocumentTypes()
  }

  @computed
  get totalSelected() {
    return this.documentTypeSupportingItemVms
      .concat(this.inputSupportingItemVms)
      .filter((item) => item.requested).length
  }

  @action.bound
  configureSupportingItemOptions(documentTypes: DocumentType[]) {
    const supportingItems = this._getSupportingItems()
    const clientDocTypes = documentTypes
      .filter((d) => d.uploaderUserType === 'Client')
      .sort((a, b) => a.name.localeCompare(b.name))

    this.documentTypeSupportingItemVms = clientDocTypes.map((documentType) => {
      const existing = supportingItems.find(
        (one) =>
          one.spec.type === 'Document' &&
          one.spec.documentType?.id === documentType.id,
      )
      return new SupportingItemViewModel(!!existing, !!existing?.required, {
        type: 'Document',
        documentType,
      })
    })

    this.inputSupportingItemVms = this.inputSupportingItems.map((inputItem) => {
      const existing = supportingItems.find(
        (one) => one.spec.type === inputItem.type,
      )
      return new SupportingItemViewModel(
        !!existing,
        !!existing?.required,
        inputItem,
      )
    })
  }

  @action.bound
  submit() {
    const supportingItems = this.documentTypeSupportingItemVms
      .concat(this.inputSupportingItemVms)
      .filter((item) => item.requested)
      .map((item) => {
        return {
          spec: item.spec,
          required: item.required,
        }
      })
    this._setSupportingItems(supportingItems)
    this.modal.close()
  }

  @action.bound
  show() {
    this.configureSupportingItemOptions(this.documentTypes)
    this.modal.show()
  }
}

export class SupportingItemViewModel {
  @observable accessor requested = false
  @observable accessor required = false
  readonly spec: InventoryItemSupportingItemSpec

  constructor(
    requested: boolean,
    required: boolean,
    spec: InventoryItemSupportingItemSpec,
  ) {
    this.requested = requested
    this.required = required
    this.spec = spec
  }

  @action.bound
  toggleRequired() {
    this.required = !this.required
  }

  @action.bound
  toggleRequested() {
    this.requested = !this.requested
  }

  @computed
  get key() {
    return getSupportingItemSpecKey(this.spec)
  }

  @computed
  get name() {
    return getSupportingItemSpecDisplayName(this.spec)
  }
}
